<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯門市' : '新增門市' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">處理中...</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-store-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <!-- Wixtar 同步門市提示 -->
        <div v-if="isWixtarStore" class="wixtar-managed-hint">
          <i class="ri-information-line"></i>
          <span>
            此門市由 Wixtar 同步管理，門市名稱、地址、電話為唯讀，請至 Wixtar 後台修改後重新同步；
            狀態、Email、設施、圖片、日期與倉庫設定仍可在此調整。
          </span>
        </div>

        <!-- 基本資訊 -->
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="storeCode">門市代碼</label>
              <input
                id="storeCode"
                v-model="form.storeCode"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.storeCode }"
                placeholder="例如：TPE-DA（未填寫將自動生成）"
                :disabled="isEdit"
                maxlength="20"
                @input="handleStoreCodeInput"
                @blur="handleStoreCodeBlur"
              />
              <div v-if="errors.storeCode" class="invalid-feedback">
                {{ errors.storeCode }}
              </div>
              <p v-if="isEdit" class="form-hint">門市代碼無法修改</p>
              <p v-else class="form-hint">輸入時自動轉換為大寫，未填寫將由系統自動生成</p>
            </div>

            <div class="form-group">
              <label for="storeName" class="required">門市名稱</label>
              <input
                id="storeName"
                v-model="form.storeName"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.storeName }"
                placeholder="例如：台北大安店"
                required
                maxlength="100"
                :disabled="isWixtarStore"
              />
              <div v-if="errors.storeName" class="invalid-feedback">
                {{ errors.storeName }}
              </div>
              <p v-if="isWixtarStore" class="form-hint">由 Wixtar 同步，無法修改</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="city">城市</label>
              <select
                id="city"
                v-model="form.city"
                class="form-control"
                :class="{ 'is-invalid': errors.city }"
                @change="handleCityChange"
                :disabled="loadingCities || isWixtarStore"
              >
                <option value="">請選擇城市</option>
                <option 
                  v-for="city in cities" 
                  :key="city.id" 
                  :value="city.cityName"
                  :data-city-id="city.id"
                  :data-city-code="city.cityCode"
                >
                  {{ city.cityName }}
                </option>
              </select>
              <div v-if="errors.city" class="invalid-feedback">
                {{ errors.city }}
              </div>
              <div v-if="loadingCities" class="form-hint">載入中...</div>
            </div>

            <div class="form-group">
              <label for="district">區域</label>
              <select
                id="district"
                v-model="form.district"
                class="form-control"
                :class="{ 'is-invalid': errors.district }"
                :disabled="!selectedCityId || districts.length === 0 || loadingDistricts || isWixtarStore"
                @change="handleDistrictChange"
              >
                <option value="">請選擇區域</option>
                <option 
                  v-for="district in districts" 
                  :key="district.id" 
                  :value="district.districtName"
                  :data-postal-code="district.postalCode"
                >
                  {{ district.districtName }}
                </option>
              </select>
              <div v-if="errors.district" class="invalid-feedback">
                {{ errors.district }}
              </div>
              <div v-if="loadingDistricts" class="form-hint">載入中...</div>
              <div v-else-if="!selectedCityId" class="form-hint">請先選擇城市</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label for="street">地址</label>
              <input
                id="street"
                v-model="form.street"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.street }"
                placeholder="例如：信義路四段100號"
                maxlength="255"
                :disabled="isWixtarStore"
              />
              <div v-if="errors.street" class="invalid-feedback">
                {{ errors.street }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="postalCode">郵遞區號</label>
              <input
                id="postalCode"
                v-model="form.postalCode"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.postalCode }"
                placeholder="例如：106"
                maxlength="10"
                :disabled="isWixtarStore"
              />
              <div v-if="errors.postalCode" class="invalid-feedback">
                {{ errors.postalCode }}
              </div>
            </div>

            <div class="form-group">
              <label for="status" class="required">狀態</label>
              <select
                id="status"
                v-model="form.status"
                class="form-control"
                :class="{ 'is-invalid': errors.status }"
                required
              >
                <option value="active">啟用</option>
                <option value="inactive">停用</option>
                <option value="closed">已關閉</option>
              </select>
              <div v-if="errors.status" class="invalid-feedback">
                {{ errors.status }}
              </div>
            </div>
          </div>
        </div>

        <!-- 聯絡資訊 -->
        <div class="form-section">
          <h3 class="section-title">聯絡資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="phone">電話</label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                class="form-control"
                :class="{ 'is-invalid': errors.phone }"
                placeholder="例如：02-12345678"
                maxlength="20"
                :disabled="isWixtarStore"
              />
              <div v-if="errors.phone" class="invalid-feedback">
                {{ errors.phone }}
              </div>
              <p v-if="isWixtarStore" class="form-hint">由 Wixtar 同步，無法修改</p>
            </div>

            <div class="form-group">
              <label for="email">Email</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                placeholder="例如：store@example.com"
                maxlength="100"
              />
              <div v-if="errors.email" class="invalid-feedback">
                {{ errors.email }}
              </div>
            </div>
          </div>
        </div>

        <!-- 門市設施 -->
        <div class="form-section">
          <h3 class="section-title">門市設施</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="capacity">容納人數</label>
              <input
                id="capacity"
                v-model.number="form.capacity"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.capacity }"
                placeholder="例如：50"
                min="0"
              />
              <div v-if="errors.capacity" class="invalid-feedback">
                {{ errors.capacity }}
              </div>
            </div>

            <div class="form-group">
              <label for="parkingSpaces">停車位數</label>
              <input
                id="parkingSpaces"
                v-model.number="form.parkingSpaces"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.parkingSpaces }"
                placeholder="例如：10"
                min="0"
              />
              <div v-if="errors.parkingSpaces" class="invalid-feedback">
                {{ errors.parkingSpaces }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="storeArea">門市面積（坪）</label>
              <input
                id="storeArea"
                v-model.number="form.storeArea"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.storeArea }"
                placeholder="例如：30"
                min="0"
                step="0.1"
              />
              <div v-if="errors.storeArea" class="invalid-feedback">
                {{ errors.storeArea }}
              </div>
            </div>

            <div class="form-group">
              <label for="facilities">設施說明</label>
              <input
                id="facilities"
                v-model="form.facilities"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.facilities }"
                placeholder="例如：Wi-Fi、停車場、無障礙設施"
                maxlength="255"
              />
              <div v-if="errors.facilities" class="invalid-feedback">
                {{ errors.facilities }}
              </div>
            </div>
          </div>
        </div>

        <!-- 門市圖片 -->
        <div class="form-section">
          <h3 class="section-title">門市圖片</h3>
          <p class="form-hint">可上傳 1~多張門市圖片，作為門市展示使用。</p>

          <div class="store-images-grid">
            <div
              v-for="(imageUrl, index) in form.storeImages"
              :key="`store-image-${index}`"
              class="store-image-item"
            >
              <div class="store-image-header">
                <span>圖片 {{ index + 1 }}</span>
                <button
                  type="button"
                  class="btn btn-sm btn-danger"
                  :disabled="form.storeImages.length <= 1"
                  @click="removeStoreImage(index)"
                >
                  刪除
                </button>
              </div>
              <ImageUploader
                :modelValue="imageUrl"
                :uploadFn="uploadStoreImage"
                shape="card"
                hint="建議尺寸 1200x675px"
                @update:modelValue="updateStoreImage(index, $event)"
              />
            </div>
          </div>

          <button type="button" class="btn btn-secondary btn-sm" @click="addStoreImage">
            <i class="ri-add-line"></i>
            新增圖片
          </button>
        </div>

        <!-- 日期設定 -->
        <div class="form-section">
          <h3 class="section-title">日期設定</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="openingDate">開幕日期</label>
              <input
                id="openingDate"
                v-model="form.openingDate"
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.openingDate }"
              />
              <div v-if="errors.openingDate" class="invalid-feedback">
                {{ errors.openingDate }}
              </div>
            </div>

            <div class="form-group">
              <label for="closingDate">關閉日期</label>
              <input
                id="closingDate"
                v-model="form.closingDate"
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.closingDate }"
              />
              <div v-if="errors.closingDate" class="invalid-feedback">
                {{ errors.closingDate }}
              </div>
            </div>
          </div>
        </div>

        <!-- 進銷存倉庫關聯 -->
        <div class="form-section">
          <h3 class="section-title"><i class="ri-archive-2-line"></i> 進銷存設定</h3>

          <!-- 倉庫建立模式切換（新增門市時才顯示） -->
          <div v-if="!isEdit" class="form-group">
            <label class="form-label">倉庫設定</label>
            <div class="warehouse-mode-tabs">
              <button
                type="button"
                :class="['wh-tab', warehouseMode === 'none' && 'active']"
                @click="warehouseMode = 'none'; form.warehouseId = ''"
              >不關聯倉庫</button>
              <button
                type="button"
                :class="['wh-tab', warehouseMode === 'existing' && 'active']"
                @click="warehouseMode = 'existing'; createWarehouseForm.warehouseName = ''"
              >關聯現有倉庫</button>
              <button
                type="button"
                :class="['wh-tab', warehouseMode === 'new' && 'active']"
                @click="warehouseMode = 'new'; form.warehouseId = ''"
              >同步新增倉庫</button>
            </div>
          </div>

          <!-- 關聯現有倉庫（新增模式）-->
          <div v-if="!isEdit && warehouseMode === 'existing'" class="form-group">
            <label for="warehouseId">選擇倉庫</label>
            <select id="warehouseId" v-model="form.warehouseId" class="form-control">
              <option value="">請選擇倉庫</option>
              <option v-for="w in warehouses" :key="w.id" :value="w.id">
                {{ w.warehouseName }}（{{ w.warehouseType === 'main' ? '總倉' : '分倉' }}）
              </option>
            </select>
            <div class="form-hint">啟用「依門市倉庫扣減」模式時，此門市的訂單將從對應倉庫扣除庫存</div>
          </div>

          <!-- 同步新增倉庫表單 -->
          <div v-if="!isEdit && warehouseMode === 'new'" class="new-warehouse-form">
            <div class="form-row">
              <div class="form-group">
                <label>倉庫名稱 <span class="required">*</span></label>
                <input
                  v-model="createWarehouseForm.warehouseName"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': createWarehouseErrors.warehouseName }"
                  placeholder="例：台北門市倉"
                  maxlength="100"
                />
                <div v-if="createWarehouseErrors.warehouseName" class="invalid-feedback">
                  {{ createWarehouseErrors.warehouseName }}
                </div>
              </div>
              <div class="form-group">
                <label>倉庫類型</label>
                <select v-model="createWarehouseForm.warehouseType" class="form-control">
                  <option value="sub">分倉</option>
                  <option value="main">總倉</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label>倉庫備註（選填）</label>
              <input
                v-model="createWarehouseForm.description"
                type="text"
                class="form-control"
                placeholder="例：門市現場庫存"
                maxlength="200"
              />
            </div>
            <div class="new-warehouse-hint">
              <i class="ri-information-line"></i>
              建立門市時將同步建立此倉庫，並自動關聯
            </div>
          </div>

          <!-- 編輯模式：直接顯示下拉 -->
          <div v-if="isEdit" class="form-row">
            <div class="form-group">
              <label for="warehouseId">關聯倉庫（選填）</label>
              <select id="warehouseId" v-model="form.warehouseId" class="form-control">
                <option value="">不關聯倉庫</option>
                <option v-for="w in warehouses" :key="w.id" :value="w.id">
                  {{ w.warehouseName }}（{{ w.warehouseType === 'main' ? '總倉' : '分倉' }}）
                </option>
              </select>
              <div class="form-hint">啟用「依門市倉庫扣減」模式時，此門市的訂單將從對應倉庫扣除庫存</div>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { storeService } from '@/api/modules/stores.js';
import apiClient from '@/api/axios.js';
import { cityService } from '@/api/modules/cities.js';
import { warehouseService } from '@/api/modules/warehouses.js';
import ImageUploader from '@/components/ImageUploader.vue';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'CreateStore',
  components: {
    ImageUploader
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const isEdit = computed(() => !!route.params.id);
    const { pageTitle } = usePageTitle(isEdit.value ? '編輯門市' : '新增門市');

    const form = ref({
      storeCode: '',
      storeName: '',
      city: '',
      district: '',
      street: '',
      postalCode: '',
      phone: '',
      email: '',
      status: 'active',
      capacity: null,
      parkingSpaces: null,
      storeArea: null,
      facilities: '',
      storeImages: [''],
      bookingEnabled: false,
      advanceBookingDays: 1,
      minAdvanceHours: 2,
      advanceBookingMode: 'hours',
      slotDuration: 60,
      maxDailyBookings: null,
      maxDailyBookingMode: 'count',
      requireDeposit: false,
      depositAmount: null,
      cancellationHours: 24,
      openingDate: '',
      closingDate: '',
      warehouseId: '',
    });

    const errors = ref({});
    const submitting = ref(false);
    const loading = ref(false);
    // Wixtar 同步門市：主檔欄位唯讀（修改後下次同步會被覆蓋）
    const isWixtarStore = ref(false);
    const cities = ref([]);
    const districts = ref([]);
    const warehouses = ref([]);

    // 新增門市時的倉庫建立模式
    const warehouseMode = ref('none'); // 'none' | 'existing' | 'new'
    const createWarehouseForm = ref({
      warehouseName: '',
      warehouseType: 'sub',
      description: '',
    });
    const createWarehouseErrors = ref({});
    const selectedCityId = ref(null);
    const loadingCities = ref(false);
    const loadingDistricts = ref(false);

    const generateStoreCode = () => {
      // 如果沒有門市名稱，使用預設值
      let prefix = 'STR';
      
      if (form.value.storeName && form.value.storeName.trim()) {
        const name = form.value.storeName.trim();
        const nameUpper = name.toUpperCase();
        // 移除所有非字母數字字符，只保留字母和數字
        const cleaned = nameUpper.replace(/[^A-Z0-9]/g, '');
        if (cleaned.length >= 2) {
          // 最多取3個字符作為前綴
          prefix = cleaned.substring(0, 3);
        }
      }
      
      // 計算可用數字長度（總長度不超過10碼，減去前綴長度）
      const prefixLength = prefix.length;
      const numberLength = 10 - prefixLength;
      
      // 使用時間戳後幾位確保唯一性
      const timestamp = Date.now().toString();
      // 取時間戳後 numberLength 位
      const numberPart = timestamp.slice(-numberLength);
      
      return prefix + numberPart;
    };

    const handleStoreCodeInput = (event) => {
      // 自動轉換為大寫
      const value = event.target.value.toUpperCase();
      form.value.storeCode = value;
    };

    const handleStoreCodeBlur = () => {
      // 失去焦點時，如果為空則自動生成
      if (!form.value.storeCode || !form.value.storeCode.trim()) {
        form.value.storeCode = generateStoreCode();
      } else {
        // 確保是大寫
        form.value.storeCode = form.value.storeCode.toUpperCase().trim();
      }
    };

    const validateForm = () => {
      errors.value = {};

      // 如果門市代碼為空，自動生成
      if (!form.value.storeCode || !form.value.storeCode.trim()) {
        form.value.storeCode = generateStoreCode();
      } else {
        // 確保是大寫
        form.value.storeCode = form.value.storeCode.toUpperCase().trim();
      }

      if (!form.value.storeName || !form.value.storeName.trim()) {
        errors.value.storeName = '請輸入門市名稱';
      }

      if (form.value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.email)) {
        errors.value.email = 'Email 格式不正確';
      }

      return Object.keys(errors.value).length === 0;
    };

    // 將日期字串轉換為 YYYY-MM-DD 格式（用於 input[type="date"]）
    const formatDateForInput = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().split('T')[0];
      } catch (e) {
        return '';
      }
    };

    const loadStore = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      try {
        const response = await storeService.getStore(route.params.id);
        if (response && response.success && response.data) {
          const store = response.data;
          isWixtarStore.value = store.dataSource === 'wixtar';
          form.value = {
            storeCode: store.storeCode || '',
            storeName: store.storeName || '',
            city: store.city || '',
            district: store.district || '',
            street: store.street || '',
            postalCode: store.postalCode || '',
            phone: store.phone || '',
            email: store.email || '',
            status: store.status || 'active',
            capacity: store.capacity || null,
            parkingSpaces: store.parkingSpaces || null,
            storeArea: store.storeArea || null,
            facilities: store.facilities || '',
            storeImages: Array.isArray(store.storeImages) && store.storeImages.length > 0 ? store.storeImages : [''],
            bookingEnabled: store.bookingEnabled || false,
            advanceBookingDays: store.advanceBookingDays ?? 1,
            minAdvanceHours: store.minAdvanceHours ?? 2,
            advanceBookingMode: store.advanceBookingMode || 'hours',
            slotDuration: store.slotDuration || 60,
            maxDailyBookings: store.maxDailyBookings ?? null,
            maxDailyBookingMode: store.maxDailyBookingMode || 'count',
            requireDeposit: store.requireDeposit || false,
            depositAmount: store.depositAmount || null,
            cancellationHours: store.cancellationHours || 24,
            openingDate: formatDateForInput(store.openingDate),
            closingDate: formatDateForInput(store.closingDate),
            warehouseId: store.warehouseId || '',
          };

        } else {
          await showError('載入失敗', '無法載入門市資料');
          router.push('/client/stores');
        }
      } catch (err) {
        console.error('載入門市失敗:', err);
        await showError('載入失敗', err.message || '無法載入門市資料');
        router.push('/client/stores');
      } finally {
        loading.value = false;
      }
    };

    const validateNewWarehouse = () => {
      createWarehouseErrors.value = {};
      if (warehouseMode.value === 'new' && !createWarehouseForm.value.warehouseName.trim()) {
        createWarehouseErrors.value.warehouseName = '請輸入倉庫名稱';
        return false;
      }
      return true;
    };

    const uploadStoreImage = async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    };

    const addStoreImage = () => {
      form.value.storeImages.push('');
    };

    const removeStoreImage = (index) => {
      if (form.value.storeImages.length <= 1) return;
      form.value.storeImages.splice(index, 1);
    };

    const updateStoreImage = (index, imageUrl) => {
      form.value.storeImages[index] = imageUrl || '';
    };

    const handleSubmit = async () => {
      if (!validateForm() || !validateNewWarehouse()) {
        return;
      }

      submitting.value = true;
      errors.value = {};

      try {
        // 若選擇「同步新增倉庫」，先建立倉庫取得 ID
        if (!isEdit.value && warehouseMode.value === 'new') {
          // 自動生成倉庫代碼（WH + 時間戳末 6 位）
          const autoCode = 'WH' + Date.now().toString().slice(-6);
          const whRes = await warehouseService.createWarehouse({
            warehouseCode: autoCode,
            warehouseName: createWarehouseForm.value.warehouseName.trim(),
            warehouseType: createWarehouseForm.value.warehouseType,
            notes: createWarehouseForm.value.description.trim() || undefined,
            status: 'active',
          });
          if (whRes && whRes.data && whRes.data.id) {
            form.value.warehouseId = whRes.data.id;
          } else {
            throw new Error('建立倉庫失敗，請稍後再試');
          }
        }

        // 定義後端允許的欄位（根據 updateBody schema）
        const allowedFields = [
          'storeName', 'city', 'district', 'street', 'postalCode',
          'latitude', 'longitude', 'phone', 'email', 'facilities', 'storeImages',
          'capacity', 'parkingSpaces', 'storeArea', 'bookingEnabled',
          'advanceBookingDays', 'minAdvanceHours', 'slotDuration',
          'advanceBookingMode',
          'maxDailyBookings', 'maxDailyBookingMode', 'requireDeposit', 'depositAmount',
          'cancellationHours', 'status', 'openingDate', 'closingDate',
          'warehouseId'
        ];

        // Wixtar 同步門市：主檔欄位不送出，避免後端擋下更新
        const wixtarManagedFields = [
          'storeName', 'city', 'district', 'street', 'postalCode', 'latitude', 'longitude', 'phone'
        ];
        const submitFields = isWixtarStore.value
          ? allowedFields.filter(key => !wixtarManagedFields.includes(key))
          : allowedFields;

        const formData = {};

        // 只包含允許的欄位，並清理空值
        submitFields.forEach(key => {
          const value = form.value[key];
          if (key === 'storeImages') {
            formData[key] = (Array.isArray(value) ? value : []).filter(url => typeof url === 'string' && url.trim());
            return;
          }
          // 跳過空字串和 null，但保留 0 和 false
          if (value !== '' && value !== null && value !== undefined) {
            formData[key] = value;
          }
        });

        // 如果是新增模式，需要包含 storeCode
        if (!isEdit.value && form.value.storeCode) {
          formData.storeCode = form.value.storeCode;
        }

        let response;
        if (isEdit.value) {
          response = await storeService.updateStore(route.params.id, formData);
        } else {
          response = await storeService.createStore(formData);
        }

        if (response && response.success) {
          const warehouseCreated = !isEdit.value && warehouseMode.value === 'new';
          await showSuccess(
            isEdit.value ? '更新成功' : '建立成功',
            warehouseCreated
              ? `門市「${form.value.storeName}」已建立，並同步建立倉庫「${createWarehouseForm.value.warehouseName}」`
              : `門市「${form.value.storeName}」已${isEdit.value ? '更新' : '建立'}`
          );
          router.push('/client/stores');
        } else {
          const errorMsg = (response && response.message) || (isEdit.value ? '更新門市失敗' : '建立門市失敗');
          await showError(isEdit.value ? '更新失敗' : '建立失敗', errorMsg);
        }
      } catch (err) {
        console.error('提交表單失敗:', err);
        
        if (err.response && err.response.data && err.response.data.errors) {
          errors.value = err.response.data.errors;
        }

        let errorTitle = isEdit.value ? '更新失敗' : '建立失敗';
        let errorText = err.message || (isEdit.value ? '更新門市失敗，請稍後再試' : '建立門市失敗，請稍後再試');

        if (err.code === 'STORE_LIMIT_EXCEEDED') {
          errorText = '已達到門市數量上限，請升級方案';
        } else if (err.code === 'VALIDATION_ERROR') {
          errorText = err.message || '資料驗證失敗，請檢查輸入的資料';
        }

        await showError(errorTitle, errorText);
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/stores');
    };

    // 載入倉庫列表
    const loadWarehouses = async () => {
      try {
        const res = await warehouseService.getWarehouses({ status: 'active' });
        warehouses.value = res.data?.items || [];
      } catch (e) {
        console.error('載入倉庫列表失敗:', e);
      }
    };

    // 載入城市列表
    const loadCities = async () => {
      loadingCities.value = true;
      try {
        const response = await cityService.getCities();
        if (response && response.success && response.data) {
          cities.value = response.data;
        }
      } catch (err) {
        console.error('載入城市列表失敗:', err);
      } finally {
        loadingCities.value = false;
      }
    };

    // 載入區域列表
    const loadDistricts = async (cityId) => {
      if (!cityId) {
        districts.value = [];
        return;
      }

      loadingDistricts.value = true;
      try {
        const response = await cityService.getDistrictsByCityId(cityId);
        if (response && response.success && response.data) {
          districts.value = response.data;
        }
      } catch (err) {
        console.error('載入區域列表失敗:', err);
        districts.value = [];
      } finally {
        loadingDistricts.value = false;
      }
    };

    // 處理城市變更
    const handleCityChange = (event) => {
      const selectedOption = event.target.options[event.target.selectedIndex];
      const cityId = selectedOption.getAttribute('data-city-id');
      
      selectedCityId.value = cityId;
      form.value.city = selectedOption.value;
      
      // 清空區域和郵遞區號
      form.value.district = '';
      form.value.postalCode = '';
      districts.value = [];
      
      // 載入對應的區域
      if (cityId) {
        loadDistricts(cityId);
      }
    };

    // 處理區域變更
    const handleDistrictChange = (event) => {
      const selectedOption = event.target.options[event.target.selectedIndex];
      const postalCode = selectedOption.getAttribute('data-postal-code');
      
      form.value.district = selectedOption.value;
      
      // 自動填入郵遞區號
      if (postalCode) {
        form.value.postalCode = postalCode;
      }
    };

    // 根據城市名稱查找城市 ID（用於編輯時載入區域）
    const findCityIdByName = (cityName) => {
      const city = cities.value.find(c => c.cityName === cityName);
      return city ? city.id : null;
    };

    onMounted(async () => {
      await Promise.all([loadCities(), loadWarehouses()]);
      if (isEdit.value) {
        await loadStore();
        
        // 如果是在編輯模式且已有城市資料，載入對應的區域
        if (form.value.city) {
          const cityId = findCityIdByName(form.value.city);
          if (cityId) {
            selectedCityId.value = cityId;
            await loadDistricts(cityId);
          }
        }
      }
    });

    return {
      form,
      errors,
      submitting,
      loading,
      isEdit,
      isWixtarStore,
      pageTitle,
      cities,
      districts,
      warehouses,
      selectedCityId,
      loadingCities,
      loadingDistricts,
      warehouseMode,
      createWarehouseForm,
      createWarehouseErrors,
      handleStoreCodeInput,
      handleStoreCodeBlur,
      uploadStoreImage,
      addStoreImage,
      removeStoreImage,
      updateStoreImage,
      handleCityChange,
      handleDistrictChange,
      handleSubmit,
      handleCancel,
    };
  },
};
</script>

<style scoped>
.create-store-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.wixtar-managed-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 24px;
  padding: 12px 14px;
  border: 1px solid #c9d8f5;
  border-radius: 8px;
  background: #f8faff;
  font-size: 13px;
  line-height: 1.6;
  color: #38507a;
}

.wixtar-managed-hint i {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 16px;
}

.form-control:disabled {
  background-color: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

label {
  font-weight: 500;
  margin-bottom: 6px;
  font-size: 14px;
}

label.required::after {
  content: ' *';
  color: #ef4444;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-control.is-invalid {
  border-color: #ef4444;
}

.invalid-feedback {
  color: #ef4444;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.store-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin: 12px 0 16px;
}

.store-image-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.store-image-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 13px;
  color: #374151;
  font-weight: 600;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6b7280;
  color: white;
}

.btn-secondary:hover {
  background-color: #4b5563;
}

.btn-danger {
  background-color: #ef4444;
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background-color: #dc2626;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}

/* 倉庫模式切換 tabs */
.warehouse-mode-tabs {
  display: flex;
  gap: 0;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
  width: fit-content;
}

.wh-tab {
  padding: 8px 18px;
  background: #fff;
  border: none;
  border-right: 1px solid #d0d5dd;
  cursor: pointer;
  font-size: 13px;
  color: #555;
  transition: background 0.15s, color 0.15s;
}

.wh-tab:last-child {
  border-right: none;
}

.wh-tab:hover {
  background: #f5f7fa;
  color: #333;
}

.wh-tab.active {
  background: #4f80e1;
  color: #fff;
  font-weight: 500;
}

/* 同步新增倉庫表單樣式 */
.new-warehouse-form {
  background: #f8faff;
  border: 1px solid #c9d8f5;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;
}

.new-warehouse-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #2a7a4b;
  margin-top: 4px;
}

.new-warehouse-hint i {
  font-size: 14px;
  flex-shrink: 0;
}
</style>
